import { User } from '../models/user';
import { createSelector, createFeatureSelector } from '@ngrx/store';
import { State } from '../reducers/users';
import { State as ClientDetailsState } from '../../clients/reducers/client-details';
import { State as ClientParentState } from '../../client-parents/reducers/client-parents';
import { UserLimiter } from '../models/user-limiter';
import { Limiter } from '../../common/enums/limiter';
import {FilterService, ListTypeFilterInstance, QueryBuilderConfigProvider, selectByListType} from "@feature/common";
import { MciUserFilterFieldProvider } from "@users/filter/mci-user-filter-field-provider";
import { ListType } from '@common/enums/list-type';

export const selectMciUserState = createFeatureSelector<State>('users');
export const selectClientDetailsState = createFeatureSelector<ClientDetailsState>('clientDetails');
export const selectClientParentState = createFeatureSelector<ClientParentState>('clientParents');

export const newUserPredicate = (u: User) => u.invitedDate == undefined && u.emailConfirmedDate == undefined;
export const unconfirmedUserPredicate = (u: User) => u.invitedDate != undefined && u.emailConfirmedDate == undefined;
export const liveUserPredicate = (u: User) => u.invitedDate != undefined && u.emailConfirmedDate != undefined;

export const selectUserCounts = createSelector(
  [selectMciUserState],
  (userState: State) => {
    return {
      'All': userState.users.length,
      'New': userState.users.filter(newUserPredicate).length,
      'Unconfirmed': userState.users.filter(unconfirmedUserPredicate).length,
      'Live': userState.users.filter(liveUserPredicate).length,
    }
  }
);

export const selectCurrentFilter = createSelector( [selectMciUserState], (userState: State) => userState.currentFilter );

export const selectFilteredUsers = createSelector(
  [selectMciUserState],
  (userState: State) => {

    switch (userState.currentFilter) {
      case 'All':
        return userState.users;
      case 'New':
        return userState.users.filter(newUserPredicate);
      case 'Unconfirmed':
        return userState.users.filter(unconfirmedUserPredicate);
      case 'Live':
        return userState.users.filter(liveUserPredicate);
      default:
        return userState.users;
    };
  }
);

export const selectLimitedByOptions = createSelector([selectMciUserState], (userState) => userState.limitedByOptions);

export const getAllUsersWithClientAndParentObjects = createSelector(
  [selectFilteredUsers, selectLimitedByOptions, selectClientDetailsState, selectClientParentState],
  (users: User[], limitedByOptions: UserLimiter[], clientDetailsState: ClientDetailsState, clientParentState: ClientParentState) => {
    if (users && !clientDetailsState.loading && !clientParentState.loading) {
      for (let usr of users) {
        usr.limitedBy = getLimitedBy(usr, limitedByOptions);
        usr.selectedClient = clientDetailsState.clients.find((x) => x.Ref === usr.clientRef);
        usr.selectedClientParent = clientParentState.clientParents.find((x) => x.Id === usr.clientParentRef);
      }
      return users;
    }
    return undefined;
  }
);

function getLimitedBy(user: User, limitedByOptions: UserLimiter[]): UserLimiter {
  if (user.clientRef && user.clientRef > 0) {
    return limitedByOptions.find((x) => x.limiter === Limiter.ClientRef);
  } else if (user.clientParentRef && user.clientParentRef > 0) {
    return limitedByOptions.find((x) => x.limiter === Limiter.ClientParentRef);
  } else {
    return null;
  }
}
